import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { RiskDirectionType } from 'models/RiskCategories';
import { EventListSortOptionValues } from 'models/SortByOptions';
import { nameof } from 'utils/typeHelpers';

export type DiseaseEventsState = {
  isLoading: boolean;
  hasError: boolean;
  disacDetailsIsLoading: boolean;
  disacDetailsHasError: boolean;
  disacDetailsData: dto.GetEventModel;
  selectedRiskType: RiskDirectionType;
  disacListData: dto.GetEventListModel;
  selectedListRiskType: RiskDirectionType;
  activityDate: string;
  disacListActivityByCountrySortBy: EventListSortOptionValues;
  disacListActivityByCountrySearchString: string;
  riskParameterCalculationBreakdown: dto.CalculationBreakdownModel;
  riskParametersIsLoading: boolean;
  riskParametersHasError: boolean;
};

export const InitialDiseaseEventsState: DiseaseEventsState = {
  isLoading: false,
  hasError: false,
  disacDetailsIsLoading: false,
  disacDetailsHasError: false,
  disacDetailsData: null,
  activityDate: null,
  selectedRiskType: 'importation',
  disacListData: null,
  disacListActivityByCountrySortBy: 'predicted-cases-of',
  disacListActivityByCountrySearchString: '',
  selectedListRiskType: 'importation',
  riskParameterCalculationBreakdown: null,
  riskParametersIsLoading: false,
  riskParametersHasError: false,
};

const diseaseActivitiesInCountryLocationsSlice = createSlice({
  name: nameof<InsightsState>('diseaseActivitiesInCountryLocations'),
  initialState: InitialDiseaseEventsState,
  reducers: {
    loadDiseaseActivitiesStart: state => {
      state.isLoading = true;
    },
    loadDiseaseActivitiesSuccess: (state, action: PayloadAction<dto.GetEventListModel>) => {
      const data = action.payload;
      state.isLoading = false;
      state.hasError = false;
      state.disacListData = data;
    },
    loadDiseaseActivitiesError: state => {
      state.isLoading = false;
      state.hasError = true;
    },
    loadDiseaseActivityDetailsStart: state => {
      state.disacDetailsIsLoading = true;
    },
    loadDiseaseActivityDetailsSuccess: (state, action: PayloadAction<dto.GetEventModel>) => {
      const data = action.payload;
      state.disacDetailsIsLoading = false;
      state.disacDetailsHasError = false;
      state.disacDetailsData = data;
    },
    loadDiseaseActivityDetailsError: state => {
      state.disacDetailsIsLoading = false;
      state.disacDetailsHasError = true;
    },
    loadRiskParametersCalculationBreakdownStart: state => {
      state.riskParametersIsLoading = true;
    },
    loadRiskParametersCalculationBreakdownSuccess: (
      state,
      action: PayloadAction<dto.GetEventModel>
    ) => {
      const data = action.payload;
      state.riskParametersIsLoading = false;
      state.riskParametersHasError = false;
      state.riskParameterCalculationBreakdown = data;
    },
    loadRiskParametersCalculationBreakdownError: state => {
      state.riskParametersIsLoading = false;
      state.riskParametersHasError = true;
    },
    setSelectedRiskType: (state, action: PayloadAction<RiskDirectionType>) => {
      state.selectedRiskType = action.payload;
    },
    setSelectedListRiskType: (state, action: PayloadAction<RiskDirectionType>) => {
      state.selectedListRiskType = action.payload;
    },
    sortByDiseaseActivityListByCountry: (
      state,
      action: PayloadAction<EventListSortOptionValues>
    ) => {
      state.disacListActivityByCountrySortBy = action.payload;
    },
    searchDiseaseActivityListActivityByCountry: (state, action: PayloadAction<string>) => {
      state.disacListActivityByCountrySearchString = action.payload;
    },
    resetDiseaseActivityListFilters: state => {
      state.disacListActivityByCountrySortBy =
        InitialDiseaseEventsState.disacListActivityByCountrySortBy;
      state.disacListActivityByCountrySearchString =
        InitialDiseaseEventsState.disacListActivityByCountrySearchString;
    },
  },
});

const { actions, reducer } = diseaseActivitiesInCountryLocationsSlice;
export const {
  loadDiseaseActivitiesStart,
  loadDiseaseActivitiesSuccess,
  loadDiseaseActivitiesError,
  loadDiseaseActivityDetailsStart,
  loadDiseaseActivityDetailsSuccess,
  loadDiseaseActivityDetailsError,
  loadRiskParametersCalculationBreakdownStart,
  loadRiskParametersCalculationBreakdownSuccess,
  loadRiskParametersCalculationBreakdownError,
  setSelectedRiskType,
  setSelectedListRiskType,
  sortByDiseaseActivityListByCountry,
  searchDiseaseActivityListActivityByCountry,
  resetDiseaseActivityListFilters,
} = actions;
export default reducer;
